import type {
  BetType,
  ExtraWallet,
  Handicap,
  Odds,
  RunnerId,
  TrueOdds
} from '@ps/isp-web-services-types';
import type { Currency } from '@ps/web-core-enums';

export enum Edge {
  EACHWAY_EDGE = 'EACHWAY_EDGE',
  PRICE_BOOST = 'PRICE_BOOST'
}

export enum OperatorOverrideType {
  PRICE_CHANGED_OVERRIDE = 'PRICE_CHANGED_OVERRIDE',
  HANDICAP_CHANGED_OVERRIDE = 'HANDICAP_CHANGED_OVERRIDE',
  SUSPENSION_OVERRIDE = 'SUSPENSION_OVERRIDE',
  EVENT_STARTED_OVERRIDE = 'EVENT_STARTED_OVERRIDE',
  MAX_STAKE_OVERRIDE = 'MAX_STAKE_OVERRIDE'
}

export interface OperatorOverride {
  operatorOverrideType: OperatorOverrideType;
  runner?: RunnerId;
}

export interface EdgeContext {
  edge: Edge;
  eachwayPlaces?: number;
  betType?: BetType;
}

export enum PriceOverlay {
  ENHANCED_PRICE_PROMOTION = 'ENHANCED_PRICE_PROMOTION'
}

export enum OptionType {
  BONUS_BALL = 'BONUS_BALL'
}

export interface RequestOptions {
  optionType: OptionType;
}

export interface BetRunner {
  runner: RunnerId & { handicap?: Handicap };
  order?: number;
  priceOverlay?: PriceOverlay;
}

export interface BetLeg {
  legType: string;
  betRunners: BetRunner[];
  winExpectedOdds?: TrueOdds;
  bsp?: boolean;
  guaranteedPrice?: boolean;
  edgeContexts?: EdgeContext[];
  isBanker?: boolean;
  options?: RequestOptions[];
}

export type PromotionId = string;

export interface BetDefinition {
  id: string;
  stakePerLine: number;
  betNo: number;
  betType: BetType;
  legs?: BetLeg[];
  takeEachway?: boolean;
  takeAccaInsurance?: boolean;
  winExpectedOdds?: Odds;
  promotions?: PromotionId[];
  edges?: Edge[];
  wallets?: ExtraWallet[];
  operatorOverrides?: OperatorOverride[];
  betReference?: string;
}

export interface PlaceBetsRequest {
  betDefinitions: BetDefinition[];
  customerRef?: string;
  acceptLowerOdds?: boolean;
  useAvailableBonus?: boolean;
  dryRun?: boolean;
  currency: Currency;
}

const placeBetRespCodes = [
  'ACCOUNT_CLOSED',
  'ACCESS_DENIED',
  'ACCOUNT_DUPLICATE_CARDS',
  'ACCOUNT_LOCKED',
  'ACCOUNT_PENDING_AUTH',
  'ACCOUNT_SECURITY_QUESTION_WRONG_3_TIMES',
  'ACCOUNT_SUSPENDED',
  'ACCOUNT_SUSPENDED_KYC',
  'BET_PLACEMENT_FAILURE',
  'INVALID_COUNTRY',
  'INVALID_PROTECTION_STATUS',
  'PARTIAL_SUCCESS',
  'REGULATOR_CLOSED',
  'SUCCESS'
] as const;
export type PlaceBetRespCode = (typeof placeBetRespCodes)[number];
export const isPlaceBetRespCode = (errCode: string): errCode is PlaceBetRespCode =>
  (placeBetRespCodes as readonly string[]).includes(errCode);

export type PlaceBetResultCode =
  | 'AML_USER_SUSPENDED'
  | 'SUCCESS'
  | 'BET_PLACEMENT_RUNNER_FAILURE'
  | 'GENERAL_FAILURE'
  | 'INSUFFICIENT_FUNDS'
  | 'OVER_LOSS_LIMIT'
  | 'OTHER_FAILURE_IN_REQUEST'
  | 'STAKE_BELOW_MINIMUM_ALLOWED'
  | 'STAKE_ABOVE_MAXIMUM_ALLOWED'
  | 'CUST_REGISTRATION_PENDING'
  | 'INVALID_BET_TYPE'
  | 'INVALID_LEG_TYPE'
  | 'DUPLICATE_BET'
  | 'STARTING_PRICE_NOT_AVAILABLE'
  | 'LIVE_PRICE_NOT_AVAILABLE'
  | 'PROMOTION_CANNOT_BE_USED'
  | 'PROMOTION_NOT_COMBINABLE'
  | 'INSUFFICIENT_LIVE_FUNDS'
  | 'EACHWAY_NOT_AVAILABLE'
  | 'ACCA_INSURANCE_NOT_AVAILABLE'
  | 'REQUESTED_PRICE_NOT_AVAILABLE'
  | 'MULTIPLE_PRICE_NOT_AVAILABLE'
  | 'GUARANTEED_PRICE_NOT_AVAILABLE'
  | 'REGULATOR_REJECTED'
  | 'STAKE_INCREMENT_INVALID'
  | 'INVALID_BET_COMBINATION'
  | 'INSUFFICIENT_VALID_FUNDS_FOR_INPLAY'
  | 'SELF_EXCLUSION'
  | 'REQUESTED_EACHWAY_PLACES_NOT_AVAILABLE'
  | 'EDGE_TERMS_NOT_AVAILABLE'
  | 'WALLET_AMOUNTS_CONSTRAINT_ERROR'
  | 'PAYOUT_ABOVE_MAXIMUM_ALLOWED'
  | 'LINES_ABOVE_MAXIMUM_ALLOWED'
  | 'INVALID_BET_DEFINITION'
  | 'PRICE_BOOST_TOKEN_NOT_AVAILABLE'
  | 'WALLET_EXPIRED'
  | 'HISTORICAL_BALANCE_EXCEEDED'
  | 'ACCOUNT_CREATED_AFTER_EVENT_START'
  | 'BLOCKGAMEPLAY_PARALLELVERTICAL'
  | 'BLOCKGAMEPLAY_TIMELIMIT'
  | 'FAILED_GEO_COMPLY_PENDING'
  | 'FAILED_GEO_COMPLY_REJECTED'
  | 'FAILED_GEO_COMPLY_SOFTWARE_BLOCKED'
  | 'FAILED_GEO_COMPLY_UNKNOWN'
  | 'FAILED_GEO_COMPLY_USING_PROXY'
  | 'DAILY_LOSS_LIMIT_CONSUMED'
  | 'DAILY_SPEND_LIMIT_CONSUMED'
  | 'MONTHLY_LOSS_LIMIT_CONSUMED'
  | 'MONTHLY_SPEND_LIMIT_CONSUMED'
  | 'WEEKLY_LOSS_LIMIT_CONSUMED'
  | 'RG_LIMITS_SETUP_REQUIRED'
  /**
   * Note this is the weekly spending limit
   * "SPEND_LIMIT_CONSUMED" == "WEEKLY_SPEND_LIMIT_CONSUMED"
   */
  | 'SPENDING_LIMIT_CONSUMED'
  | 'REGULATOR_ACTIVE_ELSEWHERE'
  | 'REGULATOR_DUPLICATE_PLAYER'
  | 'REGULATOR_FAILURE'
  | 'REGULATOR_LIMIT_REQUIRED'
  | 'REGULATOR_LONG_SESSION_ACK'
  | 'REGULATOR_PLAYER_INACTIVE'
  | 'REGULATOR_PLAYER_NOT_REGISTERED'
  | 'REGULATOR_PLAYER_WAITING_PERIOD';

export type PlaceBetRunnerFailureCode =
  | 'INVALID_GLOBAL_MARKET_ID'
  | 'INVALID_SELECTION_ID'
  | 'BET_TYPE_NOT_SUPPORTED'
  | 'MARKET_NOT_FOUND'
  | 'SELECTION_NOT_FOUND'
  | 'SELECTION_NOT_AVAILABLE_INPLAY'
  | 'MARKET_SUSPENDED'
  | 'RUNNER_SUSPENDED'
  | 'REQUESTED_PRICE_NOT_AVAILABLE'
  | 'PREPLAY_EVENT_STARTED'
  | 'REQUESTED_HANDICAP_NOT_AVAILABLE'
  | 'INVALID_COMBINATION'
  | 'DUPLICATE_RUNNER'
  | 'INVALID_LEG'
  | 'PRICE_BOOST_PRICE_NOT_AVAILABLE'
  | 'EVENT_BLOCKED_FOR_USER_JURISDICTION';

export interface PlaceBetResultRunner {
  runner: RunnerId;
  runnerOverride?: OperatorOverrideType;
  failureCode?: PlaceBetRunnerFailureCode;
  winOdds?: Odds;
}

export type PlaceBetLegFailureCode =
  | 'LEG_SUSPENDED'
  | 'INVALID_BET_LEG'
  | 'DUPLICATE_LEG'
  | 'LEG_TYPE_UNAVAILABLE'
  | 'REQUESTED_PRICE_NOT_AVAILABLE'
  | 'REQUESTED_EACHWAY_PLACES_NOT_AVAILABLE'
  | 'EDGE_TERMS_NOT_AVAILABLE'
  | 'PRICE_BOOST_PRICE_NOT_AVAILABLE';

export interface PlaceBetResultLeg {
  leg: BetLeg;
  originalWinOdds?: Odds;
  winOdds?: Odds;
  failureCode?: PlaceBetLegFailureCode;
}

export enum BetModifier {
  BPE = 'BPE',
  ACCA_INSURANCE = 'ACCA_INSURANCE',
  EACHWAY_EDGE = 'EACHWAY_EDGE',
  SGM = 'SGM',
  PRICE_BOOST = 'PRICE_BOOST',
  ENHANCED_PRICE = 'ENHANCED_PRICE',
  BONUS_BALL = 'BONUS_BALL',
  TEASER = 'TEASER'
}

export interface PlaceBetResult {
  resultCode: PlaceBetResultCode;
  betNo: number;
  betId: number;
  betReceiptId?: string;
  numLines?: number;
  totalStake?: number;
  runners?: PlaceBetResultRunner[];
  legs?: PlaceBetResultLeg[];
  totalPotentialWin?: number;
  originalTotalPotentialWin?: number;
  betPlacedTime?: string;
  betMinStake?: number;
  betMaxStake?: number;
  betMaxPayout?: number;
  betMinStakeIncrement?: number;
  usedPromotions?: PromotionId[];
  regulatorId?: string;
  betModifiers?: BetModifier;
  priceOverlay?: PriceOverlay;
  wallets?: ExtraWallet[];
  betOverrides?: OperatorOverrideType[];
  betPrice?: Odds;
  originalBetPrice?: Odds;
}

export interface PlaceBetResp {
  result?: PlaceBetResult[];
  respCode?: PlaceBetRespCode;
}
